// FILE: components/Container.tsx
// Enhanced container component with multiple width variants and responsive options

import React from 'react';

type ContainerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | 'screen';
type ContainerVariant = 'default' | 'article' | 'main' | 'aside' | 'section';

interface ContainerProps<C extends React.ElementType> {
  as?: C;
  children?: React.ReactNode;
  className?: string;
  size?: ContainerSize;
  variant?: ContainerVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  center?: boolean;
}

const sizeClasses: Record<ContainerSize, string> = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'max-w-full',
  screen: 'max-w-screen-2xl',
};

const paddingClasses: Record<string, string> = {
  none: '',
  sm: 'px-2 sm:px-4',
  md: 'px-4 sm:px-6 md:px-8',
  lg: 'px-6 sm:px-8 md:px-12',
  xl: 'px-8 sm:px-12 md:px-16',
};

const variantClasses: Record<ContainerVariant, string> = {
  default: '',
  article: 'prose prose-lg max-w-4xl',
  main: 'min-h-screen',
  aside: 'max-w-sm',
  section: 'py-8 sm:py-12',
};

const ContainerFC = <C extends React.ElementType = "div">({
  as,
  children,
  className = '',
  size = 'lg',
  variant = 'default',
  padding = 'md',
  center = true,
  ...rest
}: ContainerProps<C> & Omit<React.ComponentPropsWithoutRef<C>, keyof ContainerProps<C>>) => {
  const Component = as || "div";
  
  const baseClasses = 'w-full';
  const sizeClass = sizeClasses[size];
  const paddingClass = paddingClasses[padding];
  const variantClass = variantClasses[variant];
  const centerClass = center ? 'mx-auto' : '';
  
  const combinedClasses = [
    baseClasses,
    sizeClass,
    paddingClass,
    variantClass,
    centerClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <Component className={combinedClasses} {...rest}>
      {children}
    </Component>
  );
};

export const Container = ContainerFC;
